/**
 * Session-long Web Audio owner. It unlocks on a real gesture and deliberately
 * drops alerts while blocked instead of queuing sounds for later playback.
 */
export class InboxAlertAudioController {
  constructor({
    windowRef = typeof window === "undefined" ? null : window,
    documentRef = typeof document === "undefined" ? null : document,
    onBlocked = () => {},
  } = {}) {
    this.windowRef = windowRef;
    this.documentRef = documentRef;
    this.onBlocked = onBlocked;
    this.context = null;
    this.started = false;
    this.blockedNoticeShown = false;
    this.gestureHandler = null;
  }

  bootstrap({ onFirstGesture = () => {} } = {}) {
    if (!this.windowRef || this.started) return;
    this.started = true;
    this.gestureHandler = () => {
      this.removeGestureListeners();
      onFirstGesture();
      void this.unlock();
    };
    for (const eventName of ["pointerdown", "keydown", "touchstart"]) {
      this.windowRef.addEventListener(eventName, this.gestureHandler, { passive: true });
    }
  }

  getContext() {
    if (this.context) return this.context;
    const Context = this.windowRef?.AudioContext || this.windowRef?.webkitAudioContext;
    if (!Context) return null;
    try {
      this.context = new Context();
    } catch {
      return null;
    }
    return this.context;
  }

  async unlock({ test = false } = {}) {
    const context = this.getContext();
    if (!context) return false;
    try {
      if (context.state === "suspended") await context.resume();
      if (context.state !== "running") return false;
      this.removeGestureListeners();
      this.blockedNoticeShown = false;
      if (test) this.play();
      return true;
    } catch {
      return false;
    }
  }

  unlockAndTest() {
    return this.unlock({ test: true });
  }

  play() {
    const context = this.getContext();
    if (!context || context.state !== "running") {
      if (!this.blockedNoticeShown) {
        this.blockedNoticeShown = true;
        this.onBlocked(() => this.unlockAndTest());
      }
      return false;
    }

    try {
      const startedAt = context.currentTime;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.frequency.setValueAtTime(800, startedAt);
      oscillator.frequency.setValueAtTime(1000, startedAt + 0.1);
      gain.gain.setValueAtTime(0.3, startedAt);
      gain.gain.exponentialRampToValueAtTime(0.01, startedAt + 0.2);
      oscillator.start(startedAt);
      oscillator.stop(startedAt + 0.2);
      oscillator.onended = () => {
        try {
          oscillator.disconnect();
          gain.disconnect();
        } catch {
          // Already disconnected by the browser.
        }
      };
      return true;
    } catch {
      return false;
    }
  }

  removeGestureListeners() {
    if (!this.gestureHandler || !this.windowRef) return;
    for (const eventName of ["pointerdown", "keydown", "touchstart"]) {
      this.windowRef.removeEventListener(eventName, this.gestureHandler);
    }
    this.gestureHandler = null;
  }

  destroy() {
    this.removeGestureListeners();
    const closing = this.context?.close?.();
    if (closing?.catch) closing.catch(() => {});
    this.context = null;
    this.started = false;
  }
}
